// 2026-05-26 - FormuLearning - Step 5 §5.1 Factor/Response 입력
// 파일 위치: frontend/src/features/step5/VariableInputSection.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 Factor·Response·Mixture Total 편집 영역 비활성(fieldset).
// 2026-09-23 수정 - 이슈 #85: Mixture 선택 시 성분 합계(Mixture Total) 입력 · 범위 해석 안내(하한 반영·상한 부족 시 거부).
// 2026-11-02 수정 - 이슈 #113: Step 3 X 변수에서 사전 채운 Factor 임을 안내(저장 전).

import { useState } from 'react'
import type { Factor, DoeResponse, Step5Data } from '../../api/step5'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  factors: Factor[]
  responses: DoeResponse[]
  doeType: Step5Data['doe_type']
  mixtureTotal: number
  /** Factor 가 Step 3 X 변수에서 사전 채운 저장 전 값인지(이슈 #113) */
  prefilledFromStep3: boolean
  onMixtureTotalChange: (v: number) => void
  onFactorsChange: (f: Factor[]) => void
  onResponsesChange: (r: DoeResponse[]) => void
}

export default function VariableInputSection({ factors, responses, doeType, mixtureTotal, prefilledFromStep3, onMixtureTotalChange, onFactorsChange, onResponsesChange }: Props) {
  const canWrite = useCanWrite()
  const [localFactors, setLocalFactors] = useState<Factor[]>(factors)
  const [localResponses, setLocalResponses] = useState<DoeResponse[]>(responses)

  const updateFactor = (idx: number, field: keyof Factor, val: string | number) => {
    const next = [...localFactors]
    next[idx] = { ...next[idx], [field]: val }
    setLocalFactors(next)
    onFactorsChange(next)
  }

  const addFactor = () => {
    const next = [...localFactors, { name: '', min: 0, max: 0 }]
    setLocalFactors(next)
    onFactorsChange(next)
  }

  const removeFactor = (idx: number) => {
    const next = localFactors.filter((_, i) => i !== idx)
    setLocalFactors(next)
    onFactorsChange(next)
  }

  const updateResponse = (idx: number, field: keyof DoeResponse, val: string | number | null) => {
    const next = [...localResponses]
    next[idx] = { ...next[idx], [field]: val }
    setLocalResponses(next)
    onResponsesChange(next)
  }

  const addResponse = () => {
    const next = [...localResponses, { name: '', target: null }]
    setLocalResponses(next)
    onResponsesChange(next)
  }

  const removeResponse = (idx: number) => {
    const next = localResponses.filter((_, i) => i !== idx)
    setLocalResponses(next)
    onResponsesChange(next)
  }

  return (
    // viewer(#114) — Factor·Response·Mixture Total 편집은 모두 저장이다. 탐색 컨트롤이 없어 영역째 잠근다.
    <fieldset disabled={!canWrite} title={canWrite ? undefined : READ_ONLY_TITLE} className="mb-4 min-w-0 border-0 p-0">
      <div className="mb-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">DoE Variables (X)</h4>
          <button onClick={addFactor} className="rounded px-2 py-1 text-[10px] text-text-secondary hover:bg-bg-input" style={{ border: '1px solid var(--border-soft)' }}>
            + Factor
          </button>
        </div>
        {prefilledFromStep3 && (
          <p className="mb-2 text-[10px] text-text-secondary">
            Step 3 X Variables(Selected)의 Min/Max 를 가져왔습니다. 수정하거나 케이스를 생성하면 저장됩니다.
          </p>
        )}
        {doeType === 'mixture' && (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
            <label className="font-medium text-text-secondary" htmlFor="mixture-total">Mixture Total</label>
            <input id="mixture-total" type="number" min={0} value={mixtureTotal}
              onChange={(e) => onMixtureTotalChange(Number(e.target.value))}
              className="w-20 rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none" />
            <span className="text-[10px] text-text-secondary">
              성분 합계(요인 단위). Min 은 하한으로 반영하고, 꼭짓점이 Max 를 넘으면 생성하지 않습니다.
            </span>
          </div>
        )}
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Factor Name</th>
                <th className="w-20 px-2 py-1.5 text-center font-medium text-text-secondary">Min</th>
                <th className="w-20 px-2 py-1.5 text-center font-medium text-text-secondary">Max</th>
                <th className="w-8 px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {localFactors.map((f, i) => (
                <tr key={i} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="px-1 py-1">
                    <input type="text" value={f.name} onChange={(e) => updateFactor(i, 'name', e.target.value)}
                      className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none" placeholder="e.g. SLS (%)" />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" value={f.min} onChange={(e) => updateFactor(i, 'min', Number(e.target.value))}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none" />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" value={f.max} onChange={(e) => updateFactor(i, 'max', Number(e.target.value))}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none" />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <button onClick={() => removeFactor(i)} className="text-text-secondary hover:text-danger text-[10px]">✕</button>
                  </td>
                </tr>
              ))}
              {localFactors.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-3 text-center text-text-secondary text-[11px]">No factors. Click "+ Factor".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">DoE Responses (Y)</h4>
          <button onClick={addResponse} className="rounded px-2 py-1 text-[10px] text-text-secondary hover:bg-bg-input" style={{ border: '1px solid var(--border-soft)' }}>
            + Response
          </button>
        </div>
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Response Name</th>
                <th className="w-24 px-2 py-1.5 text-center font-medium text-text-secondary">Target</th>
                <th className="w-8 px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {localResponses.map((r, i) => (
                <tr key={i} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="px-1 py-1">
                    <input type="text" value={r.name} onChange={(e) => updateResponse(i, 'name', e.target.value)}
                      className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none" placeholder="e.g. Hardness" />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" value={r.target ?? ''} onChange={(e) => updateResponse(i, 'target', e.target.value ? Number(e.target.value) : null)}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none" placeholder="—" />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <button onClick={() => removeResponse(i)} className="text-text-secondary hover:text-danger text-[10px]">✕</button>
                  </td>
                </tr>
              ))}
              {localResponses.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-3 text-center text-text-secondary text-[11px]">No responses. Click "+ Response".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </fieldset>
  )
}
